import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldError } from "@/components/auth/FieldError";
import { Loader2 } from "lucide-react";
import { sanitizeText } from "@/utils/sanitize";
import { supabase } from "@/integrations/supabase/client";
import { buildSignupStartedPayload } from "@/utils/marketing";

interface SignupFormProps {
  email: string;
  onSubmit: (data: SignupFormData) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

export interface SignupFormData {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
}

const COUNTRIES = [
  { code: "US", name: "United States", currency: "USD" },
  { code: "GB", name: "United Kingdom", currency: "GBP" },
  { code: "EU", name: "European Union", currency: "EUR" },
  { code: "CA", name: "Canada", currency: "CAD" },
  { code: "AU", name: "Australia", currency: "AUD" },
  { code: "NZ", name: "New Zealand", currency: "NZD" },
  { code: "JP", name: "Japan", currency: "JPY" },
  { code: "IN", name: "India", currency: "INR" },
  { code: "OTHER", name: "Other", currency: "USD" },
];

export function SignupForm({ email, onSubmit, onBack, isLoading }: SignupFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(true); // Pre-accepted
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-detect country based on browser locale/timezone
  useEffect(() => {
    const detectCountry = () => {
      try {
        // Try to get country from browser locale
        const locale = navigator.language || 'en-US';
        const countryCode = locale.split('-')[1]?.toUpperCase();
        
        // Map common country codes to our COUNTRIES list
        const countryMap: Record<string, string> = {
          'US': 'US',
          'GB': 'GB',
          'UK': 'GB',
          'CA': 'CA',
          'AU': 'AU',
          'NZ': 'NZ',
          'JP': 'JP',
          'IN': 'IN',
          // European countries map to EU
          'DE': 'EU', 'FR': 'EU', 'IT': 'EU', 'ES': 'EU', 'NL': 'EU',
          'BE': 'EU', 'AT': 'EU', 'PT': 'EU', 'IE': 'EU', 'SE': 'EU',
          'DK': 'EU', 'FI': 'EU', 'GR': 'EU', 'PL': 'EU', 'CZ': 'EU'
        };
        
        const detectedCountry = countryMap[countryCode || ''] || 'OTHER';
        setCountry(detectedCountry);
        
        console.log('Auto-detected country:', detectedCountry, 'from locale:', locale);
      } catch (error) {
        console.error('Error detecting country:', error);
        setCountry('OTHER');
      }
    };
    
    detectCountry();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!country) {
      newErrors.country = "Please select your country";
    }

    if (!agreedToTerms) {
      newErrors.terms = "You must agree to the Terms & Conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const signupData = {
      email,
      firstName: sanitizeText(firstName.trim(), 100),
      lastName: sanitizeText(lastName.trim(), 100),
      country
    };

    // Fire signup_started webhook (fire and forget - before OTP is sent)
    const marketingPayload = buildSignupStartedPayload({
      email: signupData.email,
      first_name: signupData.firstName,
      country: signupData.country
    });
    
    supabase.functions.invoke('notify-signup', {
      body: marketingPayload
    }).catch(err => console.log('[SignupForm] signup_started webhook failed:', err));

    await onSubmit(signupData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-md">
      <div className="text-center space-y-4 mb-6">
        <h2 className="text-3xl font-semibold text-foreground tracking-tight" 
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
          Create Your Account
        </h2>
        <p className="text-foreground/80 text-sm" 
           style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
          You'll use this account for BUXBOX.
        </p>
      </div>

      {/* Email Display (read-only) */}
      <div>
        <label className="text-xs text-foreground/60 mb-1 block">Email</label>
        <Input
          type="email"
          value={email}
          disabled
          className="w-full h-12 bg-white/5 border-0 backdrop-blur-xl text-base text-foreground/60"
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            borderRadius: '14px / 18px'
          }}
        />
        <p className="text-xs text-foreground/60 mt-1">This email will become your BUXBOX Account.</p>
      </div>

      {/* First Name */}
      <div>
        <Input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors text-base text-foreground placeholder:text-foreground/60"
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            borderRadius: '14px / 18px'
          }}
          disabled={isLoading}
          autoFocus
        />
        {errors.firstName && <FieldError error={errors.firstName} />}
      </div>

      {/* Last Name */}
      <div>
        <Input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors text-base text-foreground placeholder:text-foreground/60"
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            borderRadius: '14px / 18px'
          }}
          disabled={isLoading}
        />
        {errors.lastName && <FieldError error={errors.lastName} />}
      </div>

      {/* Country */}
      <div>
        <Select value={country} onValueChange={setCountry} disabled={isLoading}>
          <SelectTrigger className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none text-base text-foreground"
                         style={{ 
                           fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                           borderRadius: '14px / 18px'
                         }}>
            <SelectValue placeholder="Country/Region" />
          </SelectTrigger>
          <SelectContent className="bg-background/95 backdrop-blur-xl border border-white/10">
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.country && <FieldError error={errors.country} />}
      </div>

      {/* Terms Checkbox */}
      <div className="space-y-2">
        <div className="flex items-start gap-3 py-3">
          <Checkbox
            id="terms"
            checked={agreedToTerms}
            onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
            disabled={isLoading}
            className="mt-0.5"
          />
          <label
            htmlFor="terms"
            className="text-sm text-foreground leading-snug cursor-pointer"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}
          >
            Agree to Terms & Conditions
          </label>
        </div>
        {errors.terms && <FieldError error={errors.terms} />}
        
        <p className="text-xs text-foreground/70 leading-relaxed px-4 py-3 rounded-lg bg-white/5 backdrop-blur-sm"
           style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
          By selecting Continue, you agree to the{" "}
          <a href="/terms-of-service" target="_blank" className="text-primary hover:underline">
            Terms of Service
          </a>{" "}
          and acknowledge that you will receive a verification code via email.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-3 pt-4 items-center">
        <Button
          type="submit"
          className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] tracking-[-0.3px] shadow-[0_2px_6px_rgba(0,0,0,0.1)]"
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            borderRadius: '14px / 18px'
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating Account...
            </div>
          ) : (
            "Continue"
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isLoading}
          className="w-3/4 h-14 border-0 bg-white/10 hover:bg-white/20 text-foreground font-semibold text-[17px] tracking-[-0.3px] shadow-[0_2px_6px_rgba(0,0,0,0.1)]"
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
            borderRadius: '14px / 18px'
          }}
        >
          Back
        </Button>
      </div>
    </form>
  );
}
